import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductGallery from '../components/ProductGallery';
import axios from 'axios';
import { ShoppingCart, Check, Truck, Shield, Package, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { resolveImageUrl } from '../utils/imageUrl';
import ShareProductButton from '../components/ShareProductButton';
import StarRating from '../components/StarRating';
import ReviewsSection from '../components/ReviewsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [reviewSummary, setReviewSummary] = useState({ average: 0, count: 0 });
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if product has color variants
  const hasColorVariants = useMemo(() => {
    return product?.color_variants && product.color_variants.length > 0;
  }, [product]);

  // Get current variant data or default product data
  const currentVariant = useMemo(() => {
    if (!product) return null;
    
    if (hasColorVariants) {
      const variant = product.color_variants[selectedVariantIndex];
      return {
        price: variant.price,
        original_price: variant.original_price,
        stock: variant.stock,
        color_name: variant.color_name,
        media: variant.media || []
      };
    }
    
    return {
      price: product.price,
      original_price: product.original_price,
      stock: product.stock,
      color_name: product.color,
      media: product.media || []
    };
  }, [product, selectedVariantIndex, hasColorVariants]);

  // Build a virtual product object for ProductGallery when variant is selected
  const galleryProduct = useMemo(() => {
    if (!product) return null;
    
    if (hasColorVariants && currentVariant) {
      return {
        ...product,
        media: currentVariant.media,
        image: currentVariant.media[0]?.url || product.image
      };
    }
    
    return product;
  }, [product, hasColorVariants, currentVariant]);

  const handleAddToCart = () => {
    if (product && currentVariant) {
      // Build cart item with variant info
      const cartItem = {
        ...product,
        price: currentVariant.price,
        original_price: currentVariant.original_price,
        image: hasColorVariants ? (currentVariant.media[0]?.url || product.image) : product.image,
        selectedColor: currentVariant.color_name,
        variantIndex: hasColorVariants ? selectedVariantIndex : null
      };
      addToCart(cartItem, quantity);
      toast({
        title: "Added to cart!",
        description: `${quantity}x ${product.name}${currentVariant.color_name ? ` (${currentVariant.color_name})` : ''} has been added to your cart.`,
      });
    }
  };

  const handleBuyNow = () => {
    if (product && currentVariant) {
      const buyNowItem = {
        ...product,
        price: currentVariant.price,
        original_price: currentVariant.original_price,
        image: hasColorVariants ? (currentVariant.media[0]?.url || product.image) : product.image,
        selectedColor: currentVariant.color_name,
        variantIndex: hasColorVariants ? selectedVariantIndex : null,
        quantity: quantity
      };
      // Store in sessionStorage for direct checkout without adding to cart
      sessionStorage.setItem('buyNowItem', JSON.stringify(buyNowItem));
      navigate('/checkout');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 sm:py-16 flex justify-center items-center h-64 sm:h-96">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-yellow-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 sm:py-16 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-4">Product not found</h2>
          <button onClick={() => navigate('/products')} className="text-yellow-600 hover:text-yellow-700">
            Browse all products
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Toaster />

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 p-3 sm:p-6 lg:p-8">
            {/* Product Gallery - Swipeable with multiple media */}
            <ProductGallery product={galleryProduct} />

            {/* Product Info */}
            <div className="space-y-4 sm:space-y-6 relative">
              {/* Share + Close buttons - Top right of product info box */}
              <div className="absolute top-0 right-0 flex items-center gap-2">
                <ShareProductButton productTitle={product.name} />
                <button
                  onClick={() => navigate(-1)}
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Go back"
                  data-testid="product-detail-close-btn"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                </button>
              </div>

              <div className="pr-20 sm:pr-24">
                <p className="text-yellow-600 font-semibold text-sm sm:text-base md:text-lg mb-1 sm:mb-2">{product.brand}</p>
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">{product.name}</h1>
                {/* Rating summary */}
                {(() => {
                  const avg = reviewSummary.count ? reviewSummary.average : (product.average_rating || 0);
                  const count = reviewSummary.count || product.review_count || 0;
                  if (!count && !avg) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.getElementById('reviews-anchor');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center gap-2 mb-2 hover:opacity-80 transition"
                      data-testid="product-rating-summary"
                    >
                      <StarRating value={avg} size={18} testIdPrefix="product-rating-star" />
                      <span className="text-sm font-semibold text-gray-700">
                        {avg.toFixed(1)}
                      </span>
                      <span className="text-sm text-gray-500 underline">
                        ({count} {count === 1 ? 'review' : 'reviews'})
                      </span>
                    </button>
                  );
                })()}
              </div>

              {/* Color Swatches - Only show if product has color variants */}
              {hasColorVariants && (
                <div className="border-t border-b py-3 sm:py-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Select Color:</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.color_variants.map((variant, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedVariantIndex(index)}
                        className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          selectedVariantIndex === index
                            ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                        data-testid={`color-variant-${index}`}
                      >
                        {variant.color_name}
                      </button>
                    ))}
                  </div>
                  {currentVariant?.color_name && (
                    <p className="text-sm text-gray-600 mt-2">
                      Selected: <span className="font-semibold">{currentVariant.color_name}</span>
                    </p>
                  )}
                </div>
              )}

              <div className={`${hasColorVariants ? '' : 'border-t border-b'} py-3 sm:py-4 md:py-6`}>
                <div className="flex items-baseline flex-wrap gap-2 sm:gap-4 mb-1 sm:mb-2">
                  <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
                    ₹{currentVariant?.price?.toLocaleString()}
                  </span>
                  {currentVariant?.original_price && (
                    <>
                      <span className="text-lg sm:text-xl md:text-2xl text-gray-500 line-through">
                        ₹{currentVariant.original_price.toLocaleString()}
                      </span>
                      <span className="bg-red-500 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-bold">
                        {Math.round(((currentVariant.original_price - currentVariant.price) / currentVariant.original_price) * 100)}% OFF
                      </span>
                    </>
                  )}
                </div>
                <p className="text-gray-600 text-sm sm:text-base">Inclusive of all taxes</p>
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">Description:</h3>
                <ul className="space-y-1.5 sm:space-y-2 list-disc list-inside">
                  {product.description.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <li key={idx} className="text-gray-700 text-sm sm:text-base leading-relaxed">
                      {line.trim().replace(/^[-•*]\s*/, '')}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">Features:</h3>
                  <ul className="space-y-1.5 sm:space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm sm:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stock Status - Internal only, not showing exact number */}
              <div className="bg-gray-100 rounded-lg p-3 sm:p-4">
                {currentVariant?.stock > 0 ? (
                  <p className="text-green-600 font-semibold flex items-center space-x-2 text-sm sm:text-base">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>In Stock</span>
                  </p>
                ) : (
                  <p className="text-red-600 font-semibold text-sm sm:text-base">Out of Stock</p>
                )}
              </div>

              {/* Quantity Selector */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Quantity:</label>
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold text-sm sm:text-base"
                  >
                    -
                  </button>
                  <span className="text-lg sm:text-xl font-bold w-8 sm:w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(currentVariant?.stock || 1, quantity + 1))}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold text-sm sm:text-base"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={handleAddToCart}
                  disabled={!currentVariant?.stock || currentVariant.stock === 0}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-black font-bold py-3 sm:py-4 px-4 sm:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>ADD TO CART</span>
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={!currentVariant?.stock || currentVariant.stock === 0}
                  className="flex-1 bg-black hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-3 sm:py-4 px-4 sm:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
                >
                  BUY NOW
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 pt-4 sm:pt-6 border-t">
                <div className="text-center">
                  <Truck className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-yellow-600 mb-1 sm:mb-2" />
                  <p className="text-xs sm:text-sm font-semibold">2-7 Days</p>
                  <p className="text-[10px] sm:text-xs text-gray-600">Delivery</p>
                </div>
                <div className="text-center">
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-yellow-600 mb-1 sm:mb-2" />
                  <p className="text-xs sm:text-sm font-semibold">100% Authentic</p>
                  <p className="text-[10px] sm:text-xs text-gray-600">Guaranteed</p>
                </div>
                <div className="text-center">
                  <Package className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-yellow-600 mb-1 sm:mb-2" />
                  <p className="text-xs sm:text-sm font-semibold">Free Shipping</p>
                  <p className="text-[10px] sm:text-xs text-gray-600">Above ₹2000</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews & Ratings */}
        <div id="reviews-anchor" />
        <ReviewsSection
          productId={product.id}
          productName={product.name}
          onSummaryChange={setReviewSummary}
        />
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
